import React from 'react';
import { ProgressBar, Alert, ListGroup } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faUpload, faCheck, faTimes, faSpinner, faImage, faVideo, faFile } from '@fortawesome/free-solid-svg-icons';

interface UploadStep {
  id: string;
  name: string;
  status: 'pending' | 'in-progress' | 'completed' | 'error';
  progress?: number;
  message?: string;
}

interface UploadProgressProps {
  isVisible: boolean;
  progress: number;
  error: string | null;
  message?: string;
  steps?: UploadStep[];
  currentStep?: string;
}

export const UploadProgress: React.FC<UploadProgressProps> = ({
  isVisible,
  progress,
  error,
  message = 'Processando...',
  steps = [],
  currentStep,
}) => {
  if (!isVisible) return null;

  const getStepIcon = (step: UploadStep) => {
    switch (step.id) {
      case 'track':
        return faImage;
      case 'videos':
        return faVideo;
      case 'files':
        return faFile;
      default:
        return faUpload;
    }
  };

  const getStepStatusIcon = (step: UploadStep) => {
    switch (step.status) {
      case 'completed':
        return <FontAwesomeIcon icon={faCheck} className="text-success" />;
      case 'in-progress':
        return <FontAwesomeIcon icon={faSpinner} className="text-primary fa-spin" />;
      case 'error':
        return <FontAwesomeIcon icon={faTimes} className="text-danger" />;
      default:
        return <div className="bg-light rounded-circle" style={{ width: '16px', height: '16px' }} />;
    }
  };

  return (
    <div className="upload-progress-container mb-4">
      {error ? (
        <Alert variant="danger">
          <FontAwesomeIcon icon={faTimes} className="me-2" />
          <strong>Erro no upload:</strong> {error}
        </Alert>
      ) : (
        <Alert variant="info">
          <div className="d-flex align-items-center mb-3">
            <FontAwesomeIcon icon={faUpload} className="me-2" />
            <strong>{message}</strong>
          </div>
          
          <ProgressBar 
            now={progress} 
            label={`${progress}%`}
            variant="primary"
            animated
            style={{ height: '20px' }}
            className="mb-3"
          />
          
          {steps.length > 0 && (
            <ListGroup variant="flush" className="mt-3">
              {steps.map((step) => (
                <ListGroup.Item 
                  key={step.id}
                  className={`d-flex align-items-center justify-content-between ${
                    step.id === currentStep ? 'bg-light' : ''
                  }`}
                >
                  <div className="d-flex align-items-center">
                    <FontAwesomeIcon 
                      icon={getStepIcon(step)} 
                      className="me-2 text-muted" 
                    />
                    <span className={step.status === 'completed' ? 'text-success' : ''}>
                      {step.name}
                    </span>
                    {step.message && (
                      <small className="text-muted ms-2">({step.message})</small>
                    )}
                  </div>
                  {getStepStatusIcon(step)}
                </ListGroup.Item>
              ))}
            </ListGroup>
          )}
          
          <small className="text-muted mt-2 d-block">
            {progress}% concluído
          </small>
        </Alert>
      )}
    </div>
  );
};
